import React, { useState, useEffect } from "react";
import {
  Steps,
  Button,
  Card,
  Row,
  Col,
  Space,
  Typography,
  Spin,
  notification,
} from "antd";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { FinetuneRunNew } from "../../../model_gen/finetuning";
import { DatasetService } from "../../../services/DatasetService";
import { FinetuneDataset } from "../../../model_gen/finetuning";

// Step components
import ChooseDatasetStep from "./Steps/ChooseDatasetStep";
import FinetuneDetailsStep from "./Steps/FinetuneDetailsStep";
import FinetuneSettingsStep from "./Steps/FinetuneSettingsStep";
import ReviewStep from "./Steps/ReviewStep";

const { Step } = Steps;
const { Text } = Typography;

export interface FinetuneData {
  dataset_id?: number;
  application_id?: number;
  intent_id?: number;
  intent_group_id?: number;
  prompt?: string;
  fourbit?: boolean;
  base_model?: string;
  display_name?: string;
  description?: string;
  prompt_deletions?: string[];
  warmup_steps?: number;
  learning_rate?: number;
  train_split?: number;
  selected_sub_dataset?: FinetuneDataset | null;
}

export const NEW_FINETUNE_STORAGE_KEY = "finetuneData";

const NewFinetuneRun: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [datasetLoading, setDatasetLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const step = searchParams.get("step");
    return step ? parseInt(step) : 0;
  });

  // Get dataset_id from URL if present
  const datasetIdFromUrl = searchParams.get("dataset_id");

  // Initialize finetune data from localStorage or with defaults
  const [finetuneData, setFinetuneData] = useState<FinetuneData>(() => {
    const saved = localStorage.getItem(NEW_FINETUNE_STORAGE_KEY);
    const savedData = saved
      ? JSON.parse(saved)
      : {
          fourbit: false,
          warmup_steps: 5,
          learning_rate: 2e-4,
          train_split: 0.8,
          prompt_deletions: [],
        };

    // If dataset_id is in URL, use it to override saved data
    if (datasetIdFromUrl) {
      savedData.dataset_id = parseInt(datasetIdFromUrl);

      // If dataset is provided, start at step 1 (Finetune Details)
      if (!searchParams.has("step")) {
        setCurrentStep(1);
        setSearchParams({ step: "1", dataset_id: datasetIdFromUrl });
      }
    }

    // Ensure selected_sub_dataset is properly initialized
    if (savedData.selected_sub_dataset) {
      // Convert it to a proper FinetuneDataset object if it exists
      savedData.selected_sub_dataset =
        savedData.selected_sub_dataset as FinetuneDataset;
    } else {
      savedData.selected_sub_dataset = null;
    }

    return savedData;
  });

  // Save data to localStorage whenever it changes
  useEffect(() => {
    // Create a copy of finetuneData to avoid circular references
    const dataToSave = { ...finetuneData };

    // Ensure the selected_sub_dataset is properly serialized
    if (dataToSave.selected_sub_dataset) {
      // Make sure we're only storing the necessary properties
      dataToSave.selected_sub_dataset = {
        id: dataToSave.selected_sub_dataset.id,
        name: dataToSave.selected_sub_dataset.name,
        description: dataToSave.selected_sub_dataset.description,
        application_name: dataToSave.selected_sub_dataset.application_name,
        intent_name: dataToSave.selected_sub_dataset.intent_name,
        intent_group_name: dataToSave.selected_sub_dataset.intent_group_name,
        request_count: dataToSave.selected_sub_dataset.request_count,
      };
    }

    localStorage.setItem(NEW_FINETUNE_STORAGE_KEY, JSON.stringify(dataToSave));
  }, [finetuneData]);

  // Debug log for finetuneData changes
  useEffect(() => {}, [finetuneData]);

  // Update URL when step changes, preserving dataset_id if present
  useEffect(() => {
    const params: Record<string, string> = { step: currentStep.toString() };
    if (datasetIdFromUrl) {
      params.dataset_id = datasetIdFromUrl;
    }
    setSearchParams(params);
  }, [currentStep, setSearchParams, datasetIdFromUrl]);

  // Fetch dataset details if dataset_id is provided in URL
  useEffect(() => {
    if (datasetIdFromUrl && finetuneData.dataset_id) {
      setDatasetLoading(true);
      DatasetService.getDataset(
        parseInt(datasetIdFromUrl),
        setDatasetLoading,
        apiKey
      ).subscribe(
        (dataset: FinetuneDataset) => {
          if (dataset) {
            // Update finetuneData with additional dataset information
            setFinetuneData((prev) => ({
              ...prev,
              application_id: dataset.application_id,
              intent_id: dataset.intent_id,
              intent_group_id: dataset.intent_group_id,
              // Set a default display name based on the dataset name
              display_name: prev.display_name || `${dataset.name} ft`,
            }));
          }
        },
        (error: Error) => {
          console.error("Error fetching dataset details:", error);
          notification.error({
            message: "Error",
            description: "Failed to fetch dataset details",
          });
          setDatasetLoading(false);
        }
      );
    }
  }, [datasetIdFromUrl, apiKey, finetuneData.dataset_id]);
  const steps = [
    {
      title: "Choose Dataset",
      component: (
        <ChooseDatasetStep
          selectedDataset={finetuneData.dataset_id?.toString() || ""}
          setSelectedDataset={(value: string) =>
            setFinetuneData((prev) => ({
              ...prev,
              dataset_id: parseInt(value),
            }))
          }
          isCreatingFinetune={loading}
          setFinetuneData={setFinetuneData}
        />
      ),
    },
    {
      title: "Finetune Settings",
      component: (
        <FinetuneSettingsStep
          datasetId={finetuneData.dataset_id}
          exportPrompt={finetuneData.prompt || ""}
          setExportPrompt={(value: string) =>
            setFinetuneData((prev) => ({ ...prev, prompt: value }))
          }
          fourbit={finetuneData.fourbit || false}
          setFourbit={(value: boolean) =>
            setFinetuneData((prev) => ({ ...prev, fourbit: value }))
          }
          baseModel={finetuneData.base_model || ""}
          setBaseModel={(value: string) =>
            setFinetuneData((prev) => ({ ...prev, base_model: value }))
          }
          promptDeletions={finetuneData.prompt_deletions || []}
          setPromptDeletions={(value: string[]) =>
            setFinetuneData((prev) => ({ ...prev, prompt_deletions: value }))
          }
          warmupSteps={finetuneData.warmup_steps || 5}
          setWarmupSteps={(value: number) =>
            setFinetuneData((prev) => ({ ...prev, warmup_steps: value }))
          }
          learningRate={finetuneData.learning_rate || 2e-4}
          setLearningRate={(value: number) =>
            setFinetuneData((prev) => ({ ...prev, learning_rate: value }))
          }
          trainSplit={finetuneData.train_split || 0.8}
          setTrainSplit={(value: number) =>
            setFinetuneData((prev) => ({ ...prev, train_split: value }))
          }
          selectedSubDataset={finetuneData.selected_sub_dataset || null}
          setSelectedSubDataset={(dataset: FinetuneDataset | null) =>
            setFinetuneData((prev) => ({
              ...prev,
              selected_sub_dataset: dataset,
            }))
          }
          isExporting={loading}
        />
      ),
    },
    {
      title: "Finetune Details",
      component: (
        <FinetuneDetailsStep
          displayName={finetuneData.display_name || ""}
          setDisplayName={(value: string) =>
            setFinetuneData((prev) => ({ ...prev, display_name: value }))
          }
          description={finetuneData.description || ""}
          setDescription={(value: string) =>
            setFinetuneData((prev) => ({ ...prev, description: value }))
          }
          isExporting={loading}
        />
      ),
    },
    {
      title: "Review",
      component: (
        <ReviewStep
          selectedDataset={finetuneData.dataset_id?.toString() || ""}
          exportPrompt={finetuneData.prompt || ""}
          fourbit={finetuneData.fourbit || false}
          baseModel={finetuneData.base_model || ""}
          displayName={finetuneData.display_name || ""}
          description={finetuneData.description || ""}
          promptDeletions={finetuneData.prompt_deletions || []}
          warmupSteps={finetuneData.warmup_steps || 5}
          learningRate={finetuneData.learning_rate || 2e-4}
          trainSplit={finetuneData.train_split || 0.8}
          selectedSubDataset={finetuneData.selected_sub_dataset}
        />
      ),
    },
  ];

  const canProceed = () => {
    // Don't allow proceeding if dataset is still loading
    if (datasetLoading) return false;

    switch (currentStep) {
      case 0:
        return finetuneData.dataset_id !== undefined;
      case 1:
        return (
          finetuneData.base_model !== undefined &&
          finetuneData.base_model.trim().length > 0
        );
      case 2:
        return (
          finetuneData.display_name !== undefined &&
          finetuneData.display_name.trim().length > 0
        );
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createFinetuneRun = () => {
    if (!finetuneData.dataset_id) {
      notification.error({
        message: "Missing dataset",
        description: "Please select a dataset for the finetune run",
      });
      return;
    }

    setLoading(true);

    // Create the meta object with optional sub_dataset_id
    const meta: any = {
      warmup_steps: finetuneData.warmup_steps,
      learning_rate: finetuneData.learning_rate,
      train_split: finetuneData.train_split,
      display_name: finetuneData.display_name,
    };

    // Add sub_dataset_id to meta if it exists
    if (finetuneData.selected_sub_dataset) {
      meta.sub_dataset_id = finetuneData.selected_sub_dataset.id;
    }

    // Create the FinetuneRunNew object
    const finetuneRun: FinetuneRunNew = {
      display_name: finetuneData.display_name || "",
      description: finetuneData.description || "",
      company_id: 0, // This will be set from the auth token on the server
      finetune_dataset_id: finetuneData.dataset_id,
      application_id: finetuneData.application_id,
      intent_id: finetuneData.intent_id,
      intent_group_id: finetuneData.intent_group_id,
      run_version: 0,
      status: "PENDING",
      base_model: finetuneData.base_model || "",
      prompt: finetuneData.prompt,
      remove_examples: false,
      remove_example_tag: false,
      remove_output_tag: false,
      fourbit: finetuneData.fourbit || false,
      conversation_mode: false,
      prompt_deletions: finetuneData.prompt_deletions || [],
      meta: meta,
    };

    FinetuneRunService.createFinetuneRun(
      finetuneRun,
      setLoading,
      apiKey
    ).subscribe(
      () => {
        notification.success({
          message: "Finetune run created successfully",
          description: "Your finetune run has been created successfully",
        });
        localStorage.removeItem(NEW_FINETUNE_STORAGE_KEY);
        navigate("/models/finetuning");
      },
      (error: Error) => {
        console.error("Error creating finetune run:", error);
        notification.error({
          message: "Failed to create finetune run",
          description:
            error.message ||
            "An error occurred while creating the finetune run",
        });
        setLoading(false);
      }
    );
  };

  return (
    <Card className="finetune-wrapper">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
              <Link to="/models/finetuning" style={{ color: "#828282" }}>
                Runs
                {" / "}{" "}
              </Link>
              New Run
            </Typography>
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: "24px" }}>
              Step {currentStep + 1} of {steps.length}
            </Text>
          </Col>
        </Row>

        <Steps current={currentStep} progressDot style={{ margin: "12px 0" }}>
          {steps.map((step) => (
            <Step key={step.title} title={step.title} />
          ))}
        </Steps>

        <Card
          bordered={false}
          className="step-content"
          style={{
            padding: "24px",
            borderRadius: "8px",
            margin: "12px 0",
          }}>
          {datasetLoading && currentStep === 0 ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
              <div style={{ marginTop: "16px" }}>
                Loading dataset information...
              </div>
            </div>
          ) : (
            steps[currentStep].component
          )}
        </Card>

        <Row justify="space-between">
          <Col>
            {currentStep > 0 && (
              <Button
                size="large"
                onClick={handlePrev}
                disabled={loading || datasetLoading}>
                Previous Step
              </Button>
            )}
          </Col>
          <Col>
            {currentStep < steps.length - 1 ? (
              <Button
                type="primary"
                size="large"
                onClick={handleNext}
                disabled={!canProceed() || loading || datasetLoading}>
                Continue
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                disabled={!canProceed() || loading || datasetLoading}
                onClick={createFinetuneRun}
                icon={loading && <Spin size="small" />}>
                {loading ? "Creating finetune run..." : "Create Finetune Run"}
              </Button>
            )}
          </Col>
        </Row>
      </Space>
    </Card>
  );
};

export default NewFinetuneRun;
